/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { InitialMapProps } from '@opengeoweb/core';
import { MapViewCapConnectWrapper } from '../MapViewCap/MapViewCapConnectWrapper';
import type { CapFeatures } from '../types';
import { WarningListCapConnectWrapper } from '../WarningListCap/WarningListCapConnectWrapper';

export type SupportedComponentTypes = 'CapWarningList' | 'CapWarningMap';

interface InitialProps {
  mapPreset: {
    layers?: InitialMapProps['mapPreset']['layers'];
    proj: {
      bbox: {
        left: number;
        bottom: number;
        right: number;
        top: number;
      };
      srs: string;
    };
  };
  geoJson: CapFeatures;
}

export interface ComponentsLookUpPayload {
  productConfigKey: string;
  componentType: SupportedComponentTypes;
  id: string;
  initialProps: InitialProps;
}

/**
 * The lookup table is for registering your own components with the workspace.
 * @param payload
 * @returns
 */

export const ComponentsLookUp = ({
  componentType,
  initialProps,
  productConfigKey,
  id,
}: ComponentsLookUpPayload): React.ReactElement => {
  switch (componentType) {
    case 'CapWarningMap': {
      return (
        <MapViewCapConnectWrapper
          bbox={initialProps.mapPreset.proj?.bbox}
          id={id}
          layers={initialProps.mapPreset.layers}
          productConfigKey={productConfigKey}
          srs={initialProps.mapPreset.proj?.srs}
        />
      );
    }
    case 'CapWarningList': {
      return (
        <WarningListCapConnectWrapper productConfigKey={productConfigKey} />
      );
    }
    default:
      return null!;
  }
};
